import { useEffect } from "react";
import { toast } from "sonner";

import { ApiError } from "@/api/client";
import { IconNotification } from "@/components/notifications/notifications";

/**
 * React Query `retry` predicate that skips retrying any 4xx response.
 *
 * 4xx errors (e.g. 400 missing org in JWT, 403 access forbidden) are not
 * transient — retries look like a "reload" and hide the error UI. 5xx and
 * network errors (`statusCode === 0`) still get up to 2 retries.
 */
export const noRetryOn4xx = (failureCount: number, error: Error): boolean => {
	if (
		error instanceof ApiError &&
		error.statusCode > 0 &&
		error.statusCode < 500
	) {
		return false;
	}
	return failureCount < 2;
};

interface UseQueryErrorToastArgs {
	error: Error | null | undefined;
	isError: boolean;
	title: string;
}

/**
 * Surfaces a query error as a toast notification.
 *
 * Intended for use alongside `noRetryOn4xx` so the UI distinguishes a real
 * fetch failure from a silent retry. The toast shows the error's `message`
 * as the description so backend-provided text reaches the user.
 */
export const useQueryErrorToast = ({
	error,
	isError,
	title,
}: UseQueryErrorToastArgs) => {
	useEffect(() => {
		if (isError && error) {
			toast.custom((t) => (
				<IconNotification
					color="error"
					description={error.message}
					hideDismissLabel={true}
					onClose={() => toast.dismiss(t)}
					title={title}
				/>
			));
		}
	}, [isError, error, title]);
};
